import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, Check, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function FamilyScheduleDelivery({ family }) {
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingSchedule, setExistingSchedule] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [earliestAvailableDate, setEarliestAvailableDate] = useState(null);

  useEffect(() => {
    if (family?.scheduling_invited) {
      fetchAvailableSlots(currentMonth);
      checkExistingSchedule();
    }
  }, [family, currentMonth]);

  const getMonthDateRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0); // Last day of month
    return { startDate, endDate };
  };

  const fetchAvailableSlots = async (monthDate) => {
    setLoading(true);
    try {
      const { startDate, endDate } = getMonthDateRange(monthDate);
      
      // Ensure we don't fetch past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const effectiveStartDate = startDate < today ? today : startDate;
      
      const response = await api.get('/delivery-schedules/available-slots', {
        params: {
          start_date: effectiveStartDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          family_id: family.id
        }
      });
      
      setAvailableSlots(response.data.available_slots || []);
      setEarliestAvailableDate(response.data.earliest_available_date);
    } catch (error) {
      toast.error('Failed to fetch available slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSchedule = async () => {
    try {
      const response = await api.get('/delivery-schedules');
      const schedule = response.data.find(s => s.family_id === family.id);
      setExistingSchedule(schedule);
    } catch (error) {
      console.error('Failed to check existing schedule');
    }
  };

  const handlePreviousMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    // Don't allow navigating to past months
    const today = new Date();
    if (prevMonth.getFullYear() < today.getFullYear() || 
        (prevMonth.getFullYear() === today.getFullYear() && prevMonth.getMonth() < today.getMonth())) {
      return;
    }
    
    setCurrentMonth(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentMonth(nextMonth);
  };

  const canGoPrevious = () => {
    const today = new Date();
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    return prevMonth.getFullYear() > today.getFullYear() || 
           (prevMonth.getFullYear() === today.getFullYear() && prevMonth.getMonth() >= today.getMonth());
  };

  const handleToggleSlot = (slot) => {
    const slotKey = `${slot.date}_${slot.time_slot}`;
    const exists = selectedSlots.find(s => `${s.date}_${s.time_slot}` === slotKey);
    
    if (exists) {
      setSelectedSlots(selectedSlots.filter(s => `${s.date}_${s.time_slot}` !== slotKey));
    } else {
      setSelectedSlots([...selectedSlots, { date: slot.date, time_slot: slot.time_slot }]);
    }
  };

  const handleSubmit = async () => {
    if (selectedSlots.length < 2) {
      toast.error('Please select at least 2 preferred time slots');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/delivery-schedules', {
        family_id: family.id,
        preferred_slots: selectedSlots
      });
      
      toast.success('Schedule request submitted! We\'ll confirm your delivery soon.');
      checkExistingSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit schedule');
    } finally {
      setSubmitting(false);
    }
  };

  // Group slots by date
  const groupedSlots = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.date]) acc[slot.date] = [];
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedSlots).sort();

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (!family?.scheduling_invited) {
    return null;
  }

  if (existingSchedule) {
    return (
      <Card className="border-stone-200 shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            {existingSchedule.status === 'confirmed' ? 'Delivery Scheduled!' : 'Schedule Request Submitted'}
          </CardTitle>
          <CardDescription>
            {existingSchedule.status === 'confirmed' 
              ? 'Your delivery has been confirmed' 
              : 'We\'re reviewing your preferred times'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {existingSchedule.status === 'confirmed' && existingSchedule.confirmed_slot ? (
            <div className="p-6 bg-green-50 border-2 border-green-200 rounded-lg">
              <p className="text-sm text-green-700 mb-2">Confirmed Delivery:</p>
              <p className="text-2xl font-bold text-green-900 mb-1">
                {new Date(existingSchedule.confirmed_slot.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p className="text-xl font-semibold text-green-800 mb-4">
                {existingSchedule.confirmed_slot.time_slot}
              </p>
              
              {/* Show delivery items */}
              {existingSchedule.delivery_items && existingSchedule.delivery_items.length > 0 && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <p className="text-sm font-medium text-green-800 mb-3">Items You'll Receive:</p>
                  <div className="space-y-2">
                    {existingSchedule.delivery_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white/50 px-3 py-2 rounded">
                        <span className="text-green-900">{item.item_type}</span>
                        <span className="font-semibold text-green-800">x{item.quantity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 mb-3">Your preferred time slots:</p>
              <div className="space-y-2">
                {existingSchedule.preferred_slots?.map((slot, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-900">
                      {new Date(slot.date).toLocaleDateString()} at {slot.time_slot}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-blue-600 mt-3">We'll confirm one of these times soon!</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-stone-200 shadow-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Schedule Your Delivery
        </CardTitle>
        <CardDescription>
          Select at least 2 preferred delivery times. We'll confirm one of your choices.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6 p-3 bg-stone-100 rounded-lg">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePreviousMonth}
            disabled={!canGoPrevious()}
            className="rounded-full"
            data-testid="prev-month-button"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold text-stone-800" data-testid="current-month">
            {formatMonthYear(currentMonth)}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNextMonth}
            className="rounded-full"
            data-testid="next-month-button"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading available times...</div>
        ) : sortedDates.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h4 className="text-lg font-semibold text-stone-800 mb-2">No Available Slots</h4>
            <p className="text-stone-600 mb-4">
              All delivery slots for {formatMonthYear(currentMonth)} are fully booked.
            </p>
            {earliestAvailableDate && new Date(earliestAvailableDate) > new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0) && (
              <p className="text-sm text-amber-700 mb-4">
                Note: Delivery dates are available starting{' '}
                {new Date(earliestAvailableDate).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}{' '}
                (2 weeks after your registration).
              </p>
            )}
            <Button
              variant="outline"
              onClick={handleNextMonth}
              className="rounded-full"
              data-testid="check-next-month-button"
            >
              Check {formatMonthYear(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-6 p-4 bg-accent/10 rounded-lg">
              <p className="text-sm font-medium mb-1">Selected: {selectedSlots.length} time slots</p>
              <p className="text-xs text-stone-600">
                {selectedSlots.length < 2 && `Please select ${2 - selectedSlots.length} more slot(s)`}
              </p>
              {selectedSlots.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedSlots.map((slot, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {new Date(slot.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {slot.time_slot.split(' - ')[0]}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {sortedDates.map(date => (
                <div key={date} className="border-b border-stone-200 pb-4">
                  <h4 className="font-semibold mb-3">
                    {new Date(date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {groupedSlots[date].map((slot, idx) => {
                      const slotKey = `${slot.date}_${slot.time_slot}`;
                      const isSelected = selectedSlots.some(s => `${s.date}_${s.time_slot}` === slotKey);
                      
                      return (
                        <div key={idx} className="flex items-center space-x-2">
                          <Checkbox
                            id={slotKey}
                            checked={isSelected}
                            onCheckedChange={() => handleToggleSlot(slot)}
                            data-testid={`slot-checkbox-${date}-${idx}`}
                          />
                          <Label
                            htmlFor={slotKey}
                            className={`flex-1 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border-primary text-primary' 
                                : 'hover:bg-stone-50'
                            }`}
                          >
                            <Clock className="w-3 h-3 inline mr-1" />
                            {slot.time_slot}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Info about browsing more months */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
              <p className="text-sm text-blue-700">
                Can't find a suitable time? Use the arrows above to browse other months.
              </p>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={selectedSlots.length < 2 || submitting}
              className="w-full mt-6 rounded-full"
              data-testid="submit-schedule-button"
            >
              {submitting ? 'Submitting...' : 'Submit Schedule Request'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
